// AddStudentsHelp.js — a first-run tutorial that explains the three ways to add
// students. Auto-shown the first time a teacher opens the Add-Students panel
// (gated by a per-uid localStorage flag in AddStudentsPanel), and reopenable any
// time from the "How do I add students?" link. Mirrors the WelcomeModal pattern.

import React, { useState, useEffect } from "react";
import "./AddStudentsHelp.css";

// First-run gate, shared by every "add students" surface (the Students-page panel
// and the Import Classes page). Seeing it on one surface satisfies it everywhere,
// so a teacher is never double-nagged; the modal stays reopenable on demand.
const HELP_PREFIX = "classboon_addstudents_help";

export function useAddStudentsHelp(uid) {
  const [showHelp, setShowHelp] = useState(false);
  const helpKey = uid ? `${HELP_PREFIX}_${uid}` : null;

  useEffect(() => {
    if (helpKey && localStorage.getItem(helpKey) !== "true") setShowHelp(true);
  }, [helpKey]);

  const openHelp = () => setShowHelp(true);
  const dismissHelp = () => {
    if (helpKey) localStorage.setItem(helpKey, "true");
    setShowHelp(false);
  };

  return { showHelp, openHelp, dismissHelp };
}

const METHODS = [
  {
    icon: "📋",
    tag: "Fastest",
    title: "Bulk Import",
    body:
      "Already have your roster in a spreadsheet? Copy the rows and paste them in, or upload a CSV. ClassBoon reads the columns for you — you just confirm the matches and import the whole class at once.",
  },
  {
    icon: "✏️",
    title: "Add Single",
    body:
      "Type one student at a time. Best for adding a new kid mid-year. If they're already in your school's directory, they'll pop up so you can link them in one tap.",
  },
  {
    icon: "🔗",
    title: "Join Code",
    body:
      "Let students add themselves. Share the code; on their own device they go to students.classboon.com, sign in with Google, and enter it — no typing rosters at all.",
  },
];

export default function AddStudentsHelp({ onClose }) {
  return (
    <div className="ash-overlay" role="dialog" aria-modal="true" aria-label="How to add students">
      <div className="ash-card">
        <button className="ash-close" onClick={onClose} aria-label="Close">×</button>

        <h2 className="ash-title">Three ways to add your students 👋</h2>
        <p className="ash-sub">
          Pick whatever's easiest — you can mix and match. Most teachers start by
          pasting their class list under <strong>Bulk Import</strong>.
        </p>

        <div className="ash-methods">
          {METHODS.map((m, i) => (
            <div key={m.title} className="ash-method">
              <div className="ash-method-icon">{m.icon}</div>
              <div className="ash-method-text">
                <div className="ash-method-head">
                  <span className="ash-step">{i + 1}</span>
                  <strong>{m.title}</strong>
                  {m.tag && <span className="ash-pill">{m.tag}</span>}
                </div>
                <p>{m.body}</p>
              </div>
            </div>
          ))}
        </div>

        <p className="ash-tip">
          💡 <strong>Tip:</strong> Leave <em>“Add to school directory”</em> checked so
          other teachers (art, lunch, the nurse) can sign these students out without
          re-adding them.
        </p>

        <div className="ash-actions">
          <button className="ash-primary" onClick={onClose}>Got it — let's add students</button>
        </div>
      </div>
    </div>
  );
}
